// src/portal-app/more/AccountScreen.js
//
// Plan 4 Task 22 — Account screen rewrite (P3 — grouped list).
//
// Was: two GlassCard "cards" (user info, app info) each hand-rolling its own
// label/value row markup (.acc-row / .acc-app-row), plus a full-width
// destructive Sign Out button and its ConfirmSheet.
//
// Now (P3 — spec §5: "a standard iOS grouped list: sectioned rows with label
// left, optional right-hand value, chevron. No hero, no filters, no
// cards-in-a-grid"):
//   SectionHead — plain uppercase text label per section, same as
//                 SettingsScreen's (Task 21) — no icon badge, no edit action.
//   GroupedRows — the SET fields in a section, as label/value rows.
//   EmptyFieldsPrompt — every run of consecutive unset fields in a section
//                 collapses into ONE prompt naming them — never one dash row
//                 per missing field. All-unset -> prompt only, no empty card
//                 above it. None-unset -> no prompt. This screen's fields
//                 (username, role, business) are rarely all unset in
//                 practice, but the collapse machinery is identical to every
//                 other P3/P5 screen in this plan and is exercised by
//                 dedicated fixtures in the test file.
//   PullToRefresh — spec §8. This screen has no fetch of its own (its data
//                 is derived synchronously from AuthContext), but
//                 AuthContext already exposes `refreshTenantConfig()` — the
//                 exact call SettingsScreen's PullToRefresh uses — to
//                 re-fetch the tenant config that this screen's "Business"
//                 row reads (tenantConfig.brand_name). Wiring PullToRefresh
//                 to that existing method is genuine refresh behaviour, not
//                 a decorative gesture over nothing. Disabled while the sign
//                 out sheet is open (same guard as DevicesScreen's
//                 ptrDisabled).
//
// Field-binding fix: the previous version read `getUserName()` from
// useAuth(), but AuthContext's real provider value has never exposed a
// `getUserName` function — only a `username` state field (see
// AuthContext.js's setAuthenticatedState/login, which set `username` from
// the login form / JWT `sub`). `getUserName` was undefined at runtime, so
// `getUserName ? getUserName() : ''` always took the `''` branch and the
// Username row rendered blank in the real app — a live bug, only masked in
// AccountScreen.test.js because that suite mocked `getUserName` itself.
// This rewrite binds directly to `username`.
//
// Read-only contract UNCHANGED: no profile/account editing exists in the
// web portal either, so none is added here. EmptyFieldsPrompt's onAdd
// affordance is never wired for the same reason as SettingsScreen's
// FieldBlock — there is no edit sheet on this screen for it to open.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, { useState, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import {
    GroupedRows, EmptyFieldsPrompt, PullToRefresh, ConfirmSheet, ensureKitCss,
} from '../shell/kit';
import { useNavChrome } from '../nav/NavStack';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.acc-wrap{width:100%;max-width:680px;margin-inline:auto;box-sizing:border-box;
  padding-block:8px 80px;}

/* ---- section header (title only — P3 grouped list, no icon badge, no    */
/* edit action: same treatment as SettingsScreen's SectionHead) ---- */
.acc-shead{padding-inline:16px;margin-block:18px 8px;}
.acc-shead-title{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-textLight);}
.acc-block{padding-inline:16px;margin-block-end:12px;}
.acc-block+.acc-block{margin-block-start:-4px;}

/* ---- sign out button ---- */
.acc-signout-wrap{padding-inline:16px;margin-block-start:8px;}
.acc-signout-btn{
  width:100%;min-height:52px;
  border-radius:16px;
  border:1.5px solid color-mix(in srgb,var(--c-red) 45%,transparent);
  background:color-mix(in srgb,var(--c-red) 6%,transparent);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-red);cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:8px;
  transition:background .12s ease,transform .12s ease;
}
.acc-signout-btn:hover{background:color-mix(in srgb,var(--c-red) 12%,transparent);}
.acc-signout-btn:active{transform:scale(.98);}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'acc-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Sign-out icon SVG                                                   */
/* ------------------------------------------------------------------ */
const SignOutIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* App version                                                         */
/* ------------------------------------------------------------------ */
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

/* ------------------------------------------------------------------ */
/* Sub-components                                                       */
/* ------------------------------------------------------------------ */

/** Plain section label — see file header for why there's no icon/edit slot. */
const SectionHead = ({ title }) => (
    <div className="acc-shead">
        <span className="acc-shead-title">{title}</span>
    </div>
);

/* The collapse rule (spec §1 / this task's whole point) — identical logic to
   SettingsScreen.js's FieldBlock. Partitions `fields` into set/unset. Set
   ones render as GroupedRows; every unset one collapses into ONE
   EmptyFieldsPrompt naming them — never one dash row per missing field.
   All-unset -> prompt only, no empty card above it. None-unset -> no
   prompt. No onAdd — see file header (read-only screen, no edit sheet for
   the prompt's Add button to open). */
const FieldBlock = ({ fields }) => {
    const set = fields.filter((f) => f.value);
    const unset = fields.filter((f) => !f.value);
    const rows = set.map((f) => ({ id: f.key, label: f.label, value: f.value }));

    if (rows.length === 0 && unset.length === 0) return null;

    return (
        <div className="acc-block">
            {rows.length > 0 && <GroupedRows rows={rows} />}
            {unset.length > 0 && (
                <EmptyFieldsPrompt fields={unset.map((f) => f.label)} />
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* AccountScreen                                                       */
/* ------------------------------------------------------------------ */
export default function AccountScreen() {
    ensureCss();
    ensureKitCss();

    const { t, isRTL } = useLanguage();
    // useNavChrome is called for consistency with other P3 screens (no
    // trailing actions needed here — read-only screen has nothing to put in
    // the header).
    useNavChrome(null);

    const {
        logout, username, userRole, tenantId, tenantConfig, refreshTenantConfig,
    } = useAuth();

    const [signOutOpen, setSignOutOpen] = useState(false);

    const brandName = (tenantConfig && tenantConfig.brand_name) || tenantId || '';

    const handleRefresh = useCallback(() => {
        if (typeof refreshTenantConfig !== 'function') return Promise.resolve();
        return refreshTenantConfig();
    }, [refreshTenantConfig]);

    const handleSignOutConfirm = () => {
        setSignOutOpen(false);
        logout();
    };

    /* ---- field lists, one per FieldBlock ---- */
    const accountFields = [
        { key: 'username', label: t('username_label', {}, 'Username'), value: username || null },
        { key: 'role', label: t('role_label', {}, 'Role'), value: userRole || null },
        { key: 'business', label: t('tenant_label', {}, 'Business'), value: brandName || null },
    ];

    // App identity is a constant, not fetched data — both fields are always
    // set, so this FieldBlock never produces an EmptyFieldsPrompt in
    // practice; it uses the same component for structural consistency with
    // the Account section above, not because either value can be unset.
    const appFields = [
        {
            key: 'app_name',
            label: t('app_name_label', {}, 'App Name'),
            value: t('app_name', {}, 'Aqana Manager'),
        },
        { key: 'app_version', label: t('version_label', {}, 'Version'), value: `v${APP_VERSION}` },
    ];

    return (
        <div
            className="acc-wrap"
            dir={isRTL ? 'rtl' : 'ltr'}
            data-testid="account-screen"
        >
            <PullToRefresh onRefresh={handleRefresh} disabled={signOutOpen}>

                {/* ── Account ── */}
                <div data-testid="user-info-card">
                    <SectionHead title={t('account_info', {}, 'Account')} />
                    <FieldBlock fields={accountFields} />
                </div>

                {/* ── About ── */}
                <div data-testid="app-info-card">
                    <SectionHead title={t('about_section', {}, 'About')} />
                    <FieldBlock fields={appFields} />
                </div>

                {/* ── Sign out ── */}
                <div className="acc-signout-wrap">
                    <button
                        type="button"
                        className="acc-signout-btn"
                        data-testid="sign-out-btn"
                        onClick={() => setSignOutOpen(true)}
                        aria-label={t('sign_out', {}, 'Sign Out')}
                    >
                        <SignOutIcon />
                        {t('sign_out', {}, 'Sign Out')}
                    </button>
                </div>

            </PullToRefresh>

            {/* ── Sign out confirmation ── */}
            <ConfirmSheet
                open={signOutOpen}
                onClose={() => setSignOutOpen(false)}
                onConfirm={handleSignOutConfirm}
                title={t('sign_out_confirm', {}, 'Sign Out')}
                message={t(
                    'sign_out_message',
                    {},
                    'You will be signed out of this device.',
                )}
                confirmLabel={t('sign_out', {}, 'Sign Out')}
                destructive
            />
        </div>
    );
}
